import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ATELIER, FONTS } from '../../tokens';

/**
 * BentoTile - the atomic unit of the Bento shell.
 *
 * Premium-bento craft (from Apple / Linear / Raycast):
 *   • White card on the slightly-darker canvas (#FAFAFA)
 *   • 20px radius, 24px padding, hairline border (not heavy)
 *   • Soft multi-layer shadow that lifts on hover
 *   • Optional "aurora" - a subtle violet light-bleed in the top-left
 *     corner (for HERO tiles only - rare and precious, not every tile)
 *   • Entrance stagger via `index` prop
 *   • `variant`:
 *       - 'default' : standard white tile
 *       - 'hero'    : hairline violet border + aurora glow + stronger shadow
 *       - 'dark'    : deep-ink variant (for the one "dark" tile that
 *                     anchors the shell - rare, used for credits/balance)
 *
 * Props:
 *   colSpan / rowSpan  - CSS Grid spans (1..12 cols, 1..6 rows)
 *   as                 - optional element override ('button', 'a'…)
 *   onClick            - makes the tile interactive; cursor + active scale
 *   padded             - set false to opt out of default 24px padding
 *                        (for tiles that contain their own flush content)
 *   label              - optional mono uppercase eyebrow rendered top-left
 *   cornerAction       - optional React node rendered top-right
 *   footer             - optional React node rendered bottom, separated by
 *                        a hairline rule with a leading mono caption
 *   index              - for entrance stagger (default 0)
 */
export function BentoTile({
  children,
  colSpan = 4,
  rowSpan = 1,
  variant = 'default',
  onClick,
  as,
  padded = true,
  label,
  cornerAction,
  footer,
  footerCaption,
  index = 0,
  style: extraStyle,
  className,
  ariaLabel,
}) {
  const [hover, setHover] = useState(false);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    const handleCheck = () => {
      setIsCompact(window.innerWidth < 1440 || window.innerHeight < 850);
    };
    handleCheck();
    window.addEventListener('resize', handleCheck);
    return () => window.removeEventListener('resize', handleCheck);
  }, []);

  const interactive = Boolean(onClick);
  const isDark = variant === 'dark';
  const isHero = variant === 'hero';

  const baseStyle = {
    gridColumn: `span ${colSpan}`,
    gridRow: `span ${rowSpan}`,
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
    minHeight: 0,
    background: isDark ? ATELIER.bgInk : ATELIER.bgRaised,
    color: isDark ? '#FFFFFF' : ATELIER.ink,
    border: `1px solid ${
      isHero
        ? (hover ? ATELIER.accent : ATELIER.borderAccent)
        : isDark
        ? 'rgba(255,255,255,0.06)'
        : (hover && interactive ? ATELIER.borderStrong : ATELIER.border)
    }`,
    borderRadius: 20,
    padding: padded ? (isCompact ? 14 : 24) : 0,
    cursor: interactive ? 'pointer' : 'default',
    overflow: 'hidden',
    // Layered shadow - subtle on rest, richer on hover
    boxShadow: isDark
      ? (hover ? '0 18px 40px -12px rgba(10,10,20,0.55), 0 4px 12px rgba(10,10,20,0.35)'
              : '0 8px 20px -8px rgba(10,10,20,0.40), 0 2px 6px rgba(10,10,20,0.20)')
      : isHero
      ? (hover
          ? '0 20px 44px -14px rgba(124,58,237,0.30), 0 6px 14px -4px rgba(124,58,237,0.18), 0 1px 2px rgba(10,10,20,0.04)'
          : '0 10px 30px -12px rgba(124,58,237,0.18), 0 2px 8px -2px rgba(124,58,237,0.10), 0 1px 2px rgba(10,10,20,0.04)')
      : (hover && interactive
          ? '0 10px 24px -10px rgba(10,10,20,0.16), 0 2px 6px rgba(10,10,20,0.06)'
          : '0 2px 6px rgba(10,10,20,0.04), 0 1px 2px rgba(10,10,20,0.03)'),
    transition: 'border-color 0.2s, box-shadow 0.3s, transform 0.3s',
    transform: interactive && hover ? 'translateY(-2px)' : 'translateY(0)',
    ...extraStyle,
  };

  const Tag = motion[as || 'div'] || motion.div;

  return (
    <Tag
      className={className}
      aria-label={ariaLabel}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.55,
        ease: [0.22, 1, 0.36, 1],
        delay: 0.04 + index * 0.045,
      }}
      whileTap={interactive ? { scale: 0.992 } : undefined}
      style={baseStyle}
    >
      {/* Aurora - hero tiles only */}
      {isHero && (
        <>
          <div aria-hidden style={{
            position: 'absolute',
            top: -80, left: -80,
            width: 260, height: 260,
            background: 'radial-gradient(closest-side, rgba(167,139,250,0.22) 0%, rgba(167,139,250,0) 70%)',
            pointerEvents: 'none',
            filter: 'blur(6px)',
          }} />
          <div aria-hidden style={{
            position: 'absolute',
            bottom: -120, right: -120,
            width: 320, height: 320,
            background: 'radial-gradient(closest-side, rgba(99,102,241,0.10) 0%, rgba(99,102,241,0) 70%)',
            pointerEvents: 'none',
            filter: 'blur(8px)',
          }} />
        </>
      )}

       {/* Top stripe - eyebrow label + corner action */}
      {(label || cornerAction) && (
        <div style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: isCompact ? 8 : 12,
          gap: 10,
        }}>
          {label ? (
            <span style={{
              fontFamily: FONTS.mono,
              fontSize: 10, letterSpacing: '0.24em',
              fontWeight: 700, textTransform: 'uppercase',
              color: isDark ? 'rgba(255,255,255,0.55)' : ATELIER.inkMuted,
            }}>{label}</span>
          ) : <span />}
          {cornerAction}
        </div>
      )}

      {/* Body */}
      <div style={{
        position: 'relative',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}>
        {children}
      </div>

      {/* Footer (optional) */}
      {footer && (
        <div style={{
          position: 'relative',
          marginTop: isCompact ? 10 : 16,
          paddingTop: isCompact ? 8 : 12,
          borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : ATELIER.borderSoft}`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          {footerCaption && (
            <span style={{
              fontFamily: FONTS.mono,
              fontSize: 9.5, letterSpacing: '0.22em',
              fontWeight: 700, textTransform: 'uppercase',
              color: isDark ? 'rgba(255,255,255,0.5)' : ATELIER.inkMuted,
            }}>{footerCaption}</span>
          )}
          <span style={{ flex: 1, minWidth: 0 }}>{footer}</span>
        </div>
      )}
    </Tag>
  );
}
